import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import priceListService from '@/services/priceListService'
import subscriptionService from '@/services/subscriptionService'
import holidayService from '@/services/holidayService'
import cutoffsService from '@/services/cutoffsService'
import deliveryOverlayService from '@/services/deliveryOverlayService'
import { inOneOffBlackout } from '@/utils/deliveryOverlay'
import { formatDate } from '@/utils/dateFormat'
import {
  addDaysToDateString,
  getWeekdayFromDateString,
  getChargebeeIdForDay,
  formatOrdinalDate,
} from '@/utils/deliveryDays'
import { isOneOffProduct } from '@/utils/oneOffProduct'
import AddProductModal from '@/components/AddProductModal'
// DF-286 phase 2: the back-in-stock SMS sign-up (BackInStockModal +
// accountStatusService prefill) is parked until Klaviyo is set up. Phase 1
// only blocks the add when out of stock.
import type { PriceListItem, Subscription, HolidayRequest, CutoffsPayload } from '@/types'
import '@/styles/pages/one-off-product.css'
import { addSchedules, billingFrequencyOf, mainSubscription, type AddSchedule } from '@/utils/billingFrequency'

const formatPrice = (price: number): string => `£${price.toFixed(2)}`

/** How far ahead to offer delivery dates for a one-off. */
const HORIZON_DAYS = 56

/** The URL handle is `{sku}-{slug}`; resolve on the SKU only (DF-230). */
const skuFromHandle = (handle: string | undefined): string => (handle ?? '').split('-')[0]

/** An eligible one-off delivery date the customer can pick. */
interface OneOffDate {
  date: string
  day: number
  chargebeeId: string
}

/**
 * A single product's landing page, reached from a link (a one-off campaign
 * email, or any shared product link). It shows the product and lets the
 * customer add it: a one-off (milk lockers, DF-230) through its own flow (pick
 * one of their upcoming delivery dates, a single delivery, capped per account);
 * any other product through the standard day-by-day add wizard.
 */
function ProductLandingPage(): React.JSX.Element {
  const { handle } = useParams<{ handle: string }>()
  const navigate = useNavigate()
  const sku = skuFromHandle(handle)

  const [items, setItems] = useState<PriceListItem[]>([])
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [holidays, setHolidays] = useState<HolidayRequest[]>([])
  const [cutoffs, setCutoffs] = useState<CutoffsPayload | null>(null)
  /** DF-473: date ranges one-off products can't be delivered in (Christmas). */
  const [blackouts, setBlackouts] = useState<{ from: string, to: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // One-off add state.
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [deliveredOn, setDeliveredOn] = useState<string | null>(null)

  // Standard add (normal product) state.
  const [addOpen, setAddOpen] = useState(false)
  /** The schedules a product can go on, for customers on more than one (DF-380). */
  const [schedules, setSchedules] = useState<AddSchedule[]>([])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)

    Promise.all([
      subscriptionService.getSubscriptions(),
      holidayService.getHolidays(100).then(r => r.data).catch(() => [] as HolidayRequest[]),
      cutoffsService.getCutoffs().catch(() => null),
      deliveryOverlayService.getOverlay().catch(() => null),
    ])
      .then(async ([subs, hols, cut, overlay]) => {
        // An Active real subscription first, never a CRM quotation (DF-381).
        const chosen = subs.find(s => s.statuscode === 1 && !!s.chargebee_id) ?? mainSubscription(subs)
        // Priced from the chosen subscription's frequency, so the product
        // added always matches the subscription it goes on (DF-379).
        const list = await priceListService.getPriceListItems(billingFrequencyOf(chosen))
        if (cancelled) return
        setItems(list)
        setSubscription(chosen)
        setSchedules(addSchedules(subs))
        setHolidays(hols)
        setCutoffs(cut)
        setBlackouts(overlay?.one_off_blackouts ?? [])
      })
      .catch(() => {
        if (cancelled) return
        setLoadError("We couldn't load this product just now. Please try again.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [sku])

  // Resolve the product by SKU. A single SKU (e.g. 500, the Milk Locker) has one
  // price-list row per billing frequency, and only the one-off row carries the
  // one-off flag, so prefer that row. Picking any row by SKU alone would land on
  // a normal recurring variant and add the locker as a weekly product (DF-230).
  const product = useMemo(() => {
    const matches = items.filter(i => i.product_number === sku)
    return matches.find(isOneOffProduct) ?? matches[0] ?? null
  }, [items, sku])
  const oneOff = product ? isOneOffProduct(product) : false
  // DF-286: a stock-managed one-off that has sold out. The customer can't add
  // it; they're offered a back-in-stock SMS sign-up instead.
  const outOfStock = oneOff && product?.stock_managed === true && product?.in_stock === false

  const isDateOnHoliday = useCallback(
    (dateStr: string): boolean => {
      const date = new Date(dateStr)
      return holidays.some(
        h => h.statecode === 0 && date >= new Date(h.start_date) && date <= new Date(h.end_date)
      )
    },
    [holidays]
  )

  // The customer's regular delivery weekdays, read off their standing order
  // (ignoring the surcharge and any one-off lines).
  const customerDeliveryDays = useMemo<number[]>(() => {
    if (!subscription) return []
    const days = new Set<number>()
    subscription.lines.forEach(line => {
      if (line.is_surcharge || line.is_one_off) return
      if (line.delivery_day === null || line.delivery_day === undefined) return
      days.add(line.delivery_day)
    })
    return [...days]
  }, [subscription])

  // The dates a one-off can be delivered on: the customer's own delivery days,
  // from the same lead-time cutoff as adding any other product (add_new_line),
  // that the product can reach and that isn't a booked holiday (DF-230).
  const eligibleDates = useMemo<OneOffDate[]>(() => {
    if (!product || !oneOff || customerDeliveryDays.length === 0 || !cutoffs) return []
    const floor = cutoffs.cutoffs.add_new_line.min_date
    const out: OneOffDate[] = []
    for (let i = 0; i <= HORIZON_DAYS; i++) {
      const date = addDaysToDateString(floor, i)
      const day = getWeekdayFromDateString(date)
      if (!customerDeliveryDays.includes(day)) continue
      const chargebeeId = getChargebeeIdForDay(product, day)
      if (!chargebeeId) continue
      if (isDateOnHoliday(date)) continue
      if (inOneOffBlackout(date, blackouts)) continue
      out.push({ date, day, chargebeeId })
    }
    return out
  }, [product, oneOff, customerDeliveryDays, cutoffs, isDateOnHoliday, blackouts])

  // The blocked ranges that fall among the dates offered, so the customer knows
  // why those weeks are missing (DF-473).
  const blackoutsInView = useMemo(() => {
    const floor = cutoffs?.cutoffs.add_new_line.min_date
    if (!floor) return []
    const last = addDaysToDateString(floor, HORIZON_DAYS)
    return blackouts.filter(b => b.to >= floor && b.from <= last)
  }, [blackouts, cutoffs])

  // Default the picker to the soonest eligible date once they load.
  useEffect(() => {
    if (eligibleDates.length > 0) {
      setSelectedDate(prev => (eligibleDates.some(d => d.date === prev) ? prev : eligibleDates[0].date))
    }
  }, [eligibleDates])

  // How many of this one-off the customer already holds (still-to-be-delivered
  // lines), and whether that has reached the per-account limit (DF-230).
  const heldQty = useMemo<number>(() => {
    if (!subscription || !product) return 0
    return subscription.lines
      // A removed one-off is now a cancelled line (statecode 1), not a deleted
      // row (DF-301/DF-286), so it must not count towards the per-account limit,
      // or a customer who ordered a locker then removed it could never reorder.
      .filter(l => l.is_one_off && l.product_id === product.product_id && !l.is_ended && l.statecode === 0)
      .reduce((sum, l) => sum + (l.quantity || 0), 0)
  }, [subscription, product])
  const limit = product?.one_off_limit ?? null
  const atLimit = limit !== null && heldQty >= limit

  const chosen = useMemo(
    () => eligibleDates.find(d => d.date === selectedDate) ?? null,
    [eligibleDates, selectedDate]
  )

  const handleAddOneOff = async () => {
    if (!product || !subscription || !chosen || atLimit) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await subscriptionService.createSubscriptionLine({
        subscription_id: subscription.id,
        product_id: product.product_id,
        delivery_day: chosen.day,
        quantity: 1,
        start_date: chosen.date,
        // A one-off delivers once: end it N days after the start so the next
        // weekly occurrence falls outside its window (DF-230).
        end_date: addDaysToDateString(chosen.date, product.one_off_days ?? 0),
        chargebee_id: chosen.chargebeeId,
        product_name: product.name ?? undefined,
        unit_price: product.amount,
      })
      // Pull the new line into the local copy so it shows straight away, rather
      // than waiting for the read-path TTL (it's saved in CRM regardless).
      await subscriptionService.refreshSubscriptionsUntilSettled().catch(() => undefined)
      setDeliveredOn(chosen.date)
    } catch {
      setSubmitError("Sorry, we couldn't add this to your deliveries. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  const productName = product?.name || 'this product'

  if (loading) {
    return (
      <div className="one-off-page">
        <div className="one-off-card one-off-card--loading">Loading…</div>
      </div>
    )
  }

  if (loadError || !product) {
    return (
      <div className="one-off-page">
        <div className="one-off-card">
          <h1 className="one-off-title">Product unavailable</h1>
          <p className="one-off-text">{loadError ?? "This product isn't available on your account."}</p>
          <button type="button" className="one-off-btn one-off-btn--secondary" onClick={() => navigate('/')}>
            Back to my deliveries
          </button>
        </div>
      </div>
    )
  }

  // One-off confirmation.
  if (deliveredOn) {
    return (
      <div className="one-off-page">
        <div className="one-off-card">
          <div className="one-off-tick" aria-hidden="true">✓</div>
          <h1 className="one-off-title">Added to your deliveries</h1>
          <p className="one-off-text">
            Your {productName} will be delivered on <strong>{formatOrdinalDate(deliveredOn)}</strong>. It's a
            one-off, so it comes just the once.
          </p>
          <button type="button" className="one-off-btn" onClick={() => navigate('/')}>
            View my deliveries
          </button>
        </div>
      </div>
    )
  }

  const priceEach = formatPrice(product.amount_inc_vat ?? product.amount)
  const limitCopy = limit === 1
    ? `You can order one ${productName} per account.`
    : limit
      ? `You can order up to ${limit} ${productName} per account.`
      : null

  return (
    <div className="one-off-page">
      <div className="one-off-card">
        {product.image_url && (
          <img className="one-off-image" src={product.image_url} alt={productName} />
        )}
        <h1 className="one-off-title">{productName}</h1>
        <p className="one-off-price">{priceEach}{oneOff ? ' each' : ''}</p>

        {oneOff ? (
          <>
            {outOfStock ? (
              // DF-286 phase 1: out of stock simply blocks the add. The
              // "notify me" SMS sign-up is phase 2 (pending Klaviyo setup).
              <div className="one-off-blocked" role="note">
                Sorry, {productName} is currently out of stock. Please check back soon.
              </div>
            ) : atLimit ? (
              <div className="one-off-blocked" role="note">
                You already have {limit === 1 ? `a ${productName}` : `${heldQty} of these`} on your account.
                {limitCopy ? ` ${limitCopy}` : ''}
              </div>
            ) : eligibleDates.length === 0 ? (
              <div className="one-off-blocked" role="note">
                We can't schedule this just yet. You don't have a delivery day coming up that we can add it to.
                Please pop back once you have an upcoming delivery.
              </div>
            ) : (
              <>
                <div className="one-off-field">
                  <label className="one-off-field-label" htmlFor="one-off-date">
                    When should we deliver your {productName}?
                  </label>
                  <select
                    id="one-off-date"
                    className="one-off-select"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                    disabled={submitting}
                  >
                    {eligibleDates.map(d => (
                      <option key={d.date} value={d.date}>{formatOrdinalDate(d.date)}</option>
                    ))}
                  </select>
                </div>

                <div className="one-off-goodtoknow">
                  <p className="one-off-goodtoknow-title">Good to know</p>
                  <ul>
                    {limitCopy && <li>{limitCopy}</li>}
                    {blackoutsInView.map(b => (
                      <li key={b.from}>
                        We can't deliver one-off products between {formatDate(b.from)} and {formatDate(b.to)}.
                      </li>
                    ))}
                    <li>
                      Your {productName} will be added to your invoice for the delivery week you've selected.
                    </li>
                  </ul>
                </div>

                {submitError && <p className="one-off-error">{submitError}</p>}

                <button
                  type="button"
                  className="one-off-btn"
                  onClick={handleAddOneOff}
                  disabled={!chosen || submitting}
                >
                  {submitting ? 'Adding…' : 'Confirm'}
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <p className="one-off-text">Add {productName} to your regular deliveries.</p>
            <button type="button" className="one-off-btn" onClick={() => setAddOpen(true)}>
              Add to my order
            </button>
          </>
        )}

        <button type="button" className="one-off-btn one-off-btn--secondary" onClick={() => navigate('/')}>
          Back to my deliveries
        </button>
      </div>

      {!oneOff && (
        <AddProductModal
          isOpen={addOpen}
          onClose={() => setAddOpen(false)}
          onSuccess={async () => {
            setAddOpen(false)
            navigate('/')
          }}
          subscriptionId={subscription?.id ?? ''}
          billingFrequency={billingFrequencyOf(subscription)}
          schedules={schedules}
          currentItems={subscription?.lines ?? []}
          initialProductId={product.product_id}
          initialProductSku={product.product_number}
          firstDeliveryLockDate={subscription?.is_awaiting_first_delivery ? subscription.first_delivery_date : null}
        />
      )}

    </div>
  )
}

export default ProductLandingPage
